import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { useOrganization } from "@/auth/organization";
import { cx, ErrorAlert, Icon, type IconName } from "@/components/ui";
import { portal } from "@/lib/api";
import { config } from "@/lib/config";
import { useStoreProfile, useTrack } from "@/lib/growth";

import type { StepProps } from "./index";
import { CheckMark, focusWithin, PrimaryButton, StepHeading, StepTag, surface } from "./ui";

interface Policy {
  icon: IconName;
  tile: string;
  tone: string;
  title: string;
  body: string;
  link: string;
  href: string;
}

// The intelligence limitations have no page of their own yet: the terms carry them for now.
const POLICIES: Policy[] = [
  {
    icon: "scales",
    tile: "bg-muted",
    tone: "text-fg",
    title: "Terms of Service",
    body: "The rules for using CortexGrip, including Growth Intelligence, your account and billing.",
    link: "Read Terms of Service →",
    href: `${config.marketingUrl}/terms`,
  },
  {
    icon: "shield-tick",
    tile: "bg-[#d9f0e6]",
    tone: "text-[#12a97e]",
    title: "Data & security",
    body: "What we collect, how business and source data is stored, and how long it's retained.",
    link: "Read data & security policy →",
    href: `${config.marketingUrl}/privacy`,
  },
  {
    icon: "alert-triangle",
    tile: "bg-[#fce7c6]",
    tone: "text-[#d07100]",
    title: "Intelligence limitations",
    body: "CortexGrip provides sourced business intelligence and AI-assisted interpretation. It does not replace legal, immigration, financial or other regulated professional advice, and does not guarantee complete coverage of all laws, competitors or market changes.",
    link: "Read full limitations →",
    href: `${config.marketingUrl}/terms`,
  },
];

/** Step 1 (Figma ONB-00, 4884:26530): one acceptance covers all three; nothing continues without it. */
export function TermsStep({ profile, advance }: StepProps) {
  const { orgId } = useOrganization();
  const store = useStoreProfile();
  const track = useTrack();
  const [agreed, setAgreed] = useState(profile.terms_accepted);
  const accept = useMutation({
    mutationFn: () => portal.acceptGrowthTerms(orgId),
    onSuccess: (saved) => {
      store(saved);
      track({ name: "onboarding_step_completed", properties: { step_id: "terms" } });
      advance(2);
    },
  });

  return (
    <>
      <StepTag step={1} />
      <StepHeading title="Terms, data use & limitations">
        Before we set up your Growth Intelligence profile, please review how CortexGrip handles your data and
        where AI interpretation has limits.
      </StepHeading>

      <ul className="flex flex-col gap-3.5">
        {POLICIES.map((p) => (
          <li key={p.title} className={cx("flex items-start gap-4 rounded-2xl px-5 py-[18px]", surface)}>
            <span className={cx("flex size-10 shrink-0 items-center justify-center rounded-xl", p.tile)}>
              <Icon name={p.icon} size={20} className={p.tone} />
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <p className="text-base font-semibold text-fg">{p.title}</p>
              <p className="text-sm leading-[1.8] text-fg-muted">{p.body}</p>
              <a
                href={p.href}
                target="_blank"
                rel="noreferrer"
                className="w-fit text-[13.5px] font-semibold text-brand-500 hover:text-brand-600"
              >
                {p.link}
              </a>
            </div>
          </li>
        ))}
      </ul>

      <label className={cx("flex cursor-pointer items-center gap-3 rounded-md", focusWithin)}>
        <input
          type="checkbox"
          className="sr-only"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          aria-describedby="terms-required"
        />
        <CheckMark checked={agreed} />
        <span className="flex-1 text-[14.5px] leading-[1.8] text-fg">
          I have read and accept the Terms of Service, Data &amp; Security policy and Intelligence
          Limitations.
        </span>
      </label>

      <ErrorAlert error={accept.error} />

      <div className="flex flex-col items-start gap-2.5">
        <PrimaryButton
          disabledReason={agreed ? undefined : "Accept the terms to continue."}
          loading={accept.isPending}
          onClick={() => (profile.terms_accepted ? advance(2) : accept.mutate())}
        >
          Accept and continue
        </PrimaryButton>
        <p id="terms-required" className="text-[12.5px] leading-[1.8] text-fg-subtle">
          You must accept to continue setting up your account.
        </p>
      </div>
    </>
  );
}
